import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, BadgeCheck, Building2, Compass, HandCoins,
  HeartHandshake, Lightbulb, MapPin, ShieldCheck, Sparkles, Star, TrendingUp, Users,
} from "lucide-react";
import { Reveal } from "../components/Reveal";
import ContactForm from "../components/ContactForm";
import Seo, { LOCAL_BUSINESS_JSONLD } from "../components/Seo";
import { BRAND, IMAGES } from "../lib/site";

const TRUST = [
  { v: "6+", l: "Years of Advisory" },
  { v: "2", l: "Cities · BLR & DXB" },
  { v: "100%", l: "Personalised" },
  { v: "0", l: "Brokerage Surprises" },
];

const SERVICES = [
  {
    icon: Compass,
    title: "Personalised Home Search",
    desc: "Curated project shortlisting, site visits, negotiation and end-to-end advisory until you receive your keys.",
    to: "/services#home-search",
  },
  {
    icon: TrendingUp,
    title: "Real Estate Investment",
    desc: "High-growth micro-market intelligence, pre-launch access, rental-yield strategies and NRI portfolios.",
    to: "/services#investment",
  },
  {
    icon: Building2,
    title: "Commercial Real Estate",
    desc: "Grade-A office investments, pre-leased assets, retail high-street opportunities and exit planning.",
    to: "/services#commercial",
  },
];

const WHY = [
  { icon: Sparkles, t: "Tailor-made solutions", d: "Every recommendation is built around your goal — not a developer's incentive." },
  { icon: ShieldCheck, t: "Honest, transparent guidance", d: "We share what's right for you, including the risks. Nothing hidden in fine print." },
  { icon: BadgeCheck, t: "Zero brokerage surprises", d: "Clear fees, clear deliverables. The price you're told is the price you pay." },
  { icon: Lightbulb, t: "Niche investment expertise", d: "Pre-launch access, micro-market timing and yield strategies most agents simply don't know." },
  { icon: Users, t: "Strong developer network", d: "Direct relationships with 80+ developers across Bengaluru and Dubai for early-access inventory." },
  { icon: HeartHandshake, t: "Holistic, end-to-end support", d: "From the very first call to possession, loan paperwork and rentals — we stay with you." },
];

const TESTIMONIALS = [
  {
    quote: "We were first-time buyers and overwhelmed. My Hoom narrowed our search from 18 projects to 3 — we closed in 6 weeks, on our terms.",
    author: "Anita & Rohan", role: "Homebuyers · Whitefield",
  },
  {
    quote: "Their Dubai investment thesis was the most rigorous I've seen. Pre-launch entry, 22% appreciation in 14 months.",
    author: "Suresh Iyer", role: "NRI Investor · Dubai Marina",
  },
  {
    quote: "Genuinely advisory-led. They actively talked me out of two projects that didn't suit my goal. Rare honesty.",
    author: "Priya M.", role: "HNI · North Bengaluru",
  },
];

const Home = () => (
  <>
    <Seo
      title="My Hoom — Premium Real Estate Advisory · Bengaluru & Dubai"
      path="/"
      description="Find the right move for your future. My Hoom is a premium real estate advisory and investment platform across Bengaluru and Dubai — personalised home search, investment expertise, and commercial real estate."
      jsonLd={LOCAL_BUSINESS_JSONLD}
    />
    {/* HERO */}
    <section data-testid="home-hero" className="relative min-h-[92vh] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.heroDubai} alt="Cinematic city skyline" className="w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/55 via-navy/65 to-navy/85" />
      </div>
      <div className="container-tight relative z-10 py-32">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 0.61, 0.36, 1] }} className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 bg-white/5 backdrop-blur-md">
            <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
            <span className="text-[11px] tracking-[0.22em] uppercase text-white/85">Bengaluru · Dubai · Worldwide</span>
          </div>
          <h1 data-testid="hero-headline" className="mt-7 font-serif text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
            Find the right move <br className="hidden sm:block" />
            for your <em className="text-gold not-italic font-serif">future.</em>
          </h1>
          <p className="mt-6 text-white/80 text-lg max-w-xl leading-relaxed">
            Premium, personalised real estate advisory for homebuyers and investors across India and the UAE — wealth-advisor rigour, concierge warmth.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Link to="/contact" data-testid="hero-cta-primary" className="btn-gold">
              Talk to an Advisor <ArrowRight size={16} />
            </Link>
            <Link to="/services" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 text-white/90 hover:text-gold transition-colors text-sm font-medium border-b border-white/30 hover:border-gold pb-1">
              Explore Services <ArrowUpRight size={14} />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>

    {/* TRUST BAR */}
    <section data-testid="trust-bar" className="bg-navy">
      <div className="container-tight py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-4">
          {TRUST.map((t, i) => (
            <Reveal key={t.l} delay={i * 0.08} className="text-center md:text-left">
              <div className="font-serif text-4xl md:text-5xl text-gold">{t.v}</div>
              <div className="mt-1 text-white/65 text-xs sm:text-sm tracking-wide">{t.l}</div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* SERVICES OVERVIEW */}
    <section className="py-24 lg:py-32">
      <div className="container-tight">
        <Reveal className="max-w-2xl">
          <span className="eyebrow">What we do</span>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-navy">Three doors. One partner who knows them all.</h2>
          <p className="mt-5 text-navy/65 text-lg leading-relaxed">
            Whether you're buying your first home, building a real estate portfolio, or sourcing a commercial asset — we lead the entire journey.
          </p>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1}>
              <Link to={s.to} data-testid={`service-card-${i}`} className="card-lift p-8 block h-full border border-black/5 group">
                <div className="h-12 w-12 rounded-xl bg-teal/10 text-teal grid place-items-center">
                  <s.icon size={22} strokeWidth={1.7} />
                </div>
                <h3 className="mt-6 font-serif text-2xl text-navy">{s.title}</h3>
                <p className="mt-3 text-navy/65 text-sm leading-relaxed">{s.desc}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-navy group-hover:text-gold transition-colors">
                  Learn more <ArrowRight size={14} />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* WHY MY HOOM */}
    <section className="bg-[#FAFAFA] py-24 lg:py-32 border-y border-black/5">
      <div className="container-tight">
        <div className="grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <span className="eyebrow">Why My Hoom</span>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-navy leading-tight">A new kind of real estate partner.</h2>
            <p className="mt-5 text-navy/65 leading-relaxed">
              Built like a wealth advisor, delivered like a concierge. Six principles that shape every recommendation we make.
            </p>
            <Link to="/about" className="mt-7 inline-flex items-center gap-2 text-navy hover:text-gold transition-colors text-sm font-medium border-b border-navy/20 hover:border-gold pb-1">
              Our story <ArrowUpRight size={14} />
            </Link>
          </Reveal>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {WHY.map((w, i) => (
              <Reveal key={w.t} delay={i * 0.06} className="bg-white rounded-2xl p-6 border border-black/5 hover:border-gold/30 transition-colors">
                <div className="h-10 w-10 rounded-lg bg-navy/5 text-navy grid place-items-center">
                  <w.icon size={18} strokeWidth={1.7} />
                </div>
                <h3 className="mt-4 font-serif text-xl text-navy">{w.t}</h3>
                <p className="mt-2 text-sm text-navy/65 leading-relaxed">{w.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* MARKETS */}
    <section className="py-24 lg:py-32">
      <div className="container-tight">
        <Reveal className="max-w-2xl">
          <span className="eyebrow">Markets we know intimately</span>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-navy">Two cities. Two opportunities.</h2>
        </Reveal>
        <div className="mt-14 grid lg:grid-cols-2 gap-8">
          {[
            { name: "Bengaluru", tag: "India's growth capital", img: IMAGES.bengaluru, blurb: "From Whitefield to Devanahalli — early-stage micro-market access, end-user homes and yield plays for HNIs.", href: "/markets#bengaluru" },
            { name: "Dubai", tag: "Global wealth hub", img: IMAGES.dubaiAerial, blurb: "0% capital gains, golden-visa eligibility and 6–9% gross yields make Dubai a defining diversifier for Indian capital.", href: "/markets#dubai" },
          ].map((c, i) => (
            <Reveal key={c.name} delay={i * 0.12}>
              <Link to={c.href} data-testid={`market-card-${c.name.toLowerCase()}`} className="group block relative rounded-2xl overflow-hidden h-[460px] card-lift">
                <img src={c.img} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/55 to-navy/10" />
                <div className="absolute inset-0 p-8 flex flex-col justify-end text-white">
                  <span className="eyebrow text-gold">{c.tag}</span>
                  <h3 className="mt-2 font-serif text-4xl">{c.name}</h3>
                  <p className="mt-3 text-white/80 max-w-md text-sm leading-relaxed">{c.blurb}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm text-gold border-b border-gold/40 group-hover:border-gold pb-0.5 w-fit">
                    Explore market <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* TESTIMONIALS */}
    <section className="bg-[#FAFAFA] py-24 lg:py-32 border-y border-black/5">
      <div className="container-tight">
        <Reveal className="max-w-2xl">
          <span className="eyebrow">Client voices</span>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-navy">Decisions made with confidence.</h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <article data-testid={`testimonial-${i}`} className="card-lift p-8 h-full border border-black/5 flex flex-col">
                <div className="flex gap-0.5 text-gold">
                  {Array.from({ length: 5 }).map((_, k) => <Star key={k} size={14} fill="currentColor" strokeWidth={0} />)}
                </div>
                <p className="mt-5 font-serif text-xl text-navy leading-snug">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-auto pt-6">
                  <div className="text-sm font-semibold text-navy">{t.author}</div>
                  <div className="text-xs text-navy/55 mt-0.5">{t.role}</div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* CTA STRIP with embedded form */}
    <section data-testid="home-cta-strip" className="relative bg-navy text-white py-24 lg:py-32 overflow-hidden">
      <div className="absolute -top-32 -right-32 h-[520px] w-[520px] rounded-full bg-gold/10 blur-3xl" />
      <div className="absolute -bottom-32 -left-20 h-[420px] w-[420px] rounded-full bg-teal/15 blur-3xl" />
      <div className="container-tight relative z-10 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <span className="eyebrow text-gold">Let's begin</span>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-white leading-tight">Ready to make the right move?</h2>
          <p className="mt-5 text-white/75 leading-relaxed max-w-lg">
            Tell us about your goal. We'll come back with a shortlist, a strategy, and a clear next step — usually within one business day.
          </p>
          <div className="mt-8 space-y-3 text-sm text-white/75">
            <div className="flex items-center gap-3"><HandCoins size={16} className="text-gold" /> No brokerage fee for buyer-side advisory</div>
            <div className="flex items-center gap-3"><MapPin size={16} className="text-gold" /> {BRAND.phoneIN} · Bengaluru</div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-7">
            <ContactForm variant="dark" source="home-cta-strip" compact />
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Home;
